import type { SRT_ColumnPinningState } from '@/shared/components/master-table'
import type { FieldSchemaModel } from '@/shared/generated'
import type { OnChangeFn } from '@tanstack/react-table'

import { useEffect } from 'react'
import { isEqual, uniq } from 'lodash/fp'
import { useShallow } from 'zustand/shallow'

import Sentry from '@/shared/lib/sentry'

export type StoredColumnConfig = {
	key: string
	enabled?: boolean
}

export type ColumnsConfigController = {
	columnVisibility: Record<string, boolean>
	columnPinning: SRT_ColumnPinningState
	columnOrder: string[]
	columnSizing: Record<string, number>

	setColumnVisibility: OnChangeFn<Record<string, boolean>>
	setColumnPinning: OnChangeFn<SRT_ColumnPinningState>
	setColumnOrder: OnChangeFn<string[]>
	setColumnSizing: OnChangeFn<Record<string, number>>
	restore(): void
	fromConfigs(configs: TableConfigState): void
}

export type TableConfigState = {
	columnVisibility: Record<string, boolean>
	columnPinning: { left?: string[]; right?: string[] }
	columnOrder: string[]
	columnSizing: Record<string, number>
	setColumnVisibility: (
		modifier: (state: Record<string, boolean>) => Record<string, boolean>
	) => void
	setColumnPinning: (
		modifier: (state: { left?: string[]; right?: string[] }) => {
			left?: string[]
			right?: string[]
		}
	) => void
	setColumnOrder: (columnOrder: string[]) => void
	setColumnSizing: (
		modifier: (state: Record<string, number>) => Record<string, number>
	) => void
}
type TableConfigStateValues = Pick<
	TableConfigState,
	'columnVisibility' | 'columnPinning' | 'columnOrder' | 'columnSizing'
>
const emptyTableState: TableConfigStateValues = {
	columnVisibility: {},
	columnPinning: { left: [], right: [] },
	columnOrder: [],
	columnSizing: {},
}
export function getInitialTableState(schema: FieldSchemaModel[] = []) {
	return schema.reduce(
		(acc, scheme) => {
			if (scheme) {
				acc.columnVisibility[scheme.id] = true
				acc.columnOrder.push(scheme.id)
				if (scheme.params.width) {
					acc.columnSizing[scheme.id] = scheme.params.width
				}
				if (scheme.params.locked) {
					if (!acc.columnPinning.left) {
						acc.columnPinning.left = []
					}
					acc.columnPinning.left.push(scheme.id)
				}
			}

			return acc
		},
		{
			columnVisibility: {},
			// TODO: remove this once we have a proper way to handle column pinning
			columnPinning: {
				left: ['srt-row-select', 'srt-row-expand', 'sku', 'title'],
				right: [],
			},
			columnOrder: [],
			columnSizing: {},
		} as TableConfigStateValues
	)
}

const systemColumns = [
	'srt-row-actions',
	'srt-row-drag',
	'srt-row-expand',
	'srt-row-numbers',
	'srt-row-pin',
	'srt-row-select',
	'srt-row-spacer',
]
export type TableColumnsConfigController = {
	columnVisibility: Record<string, boolean>
	columnPinning: SRT_ColumnPinningState
	columnOrder: string[]
	columnSizing: Record<string, number>
	setColumnVisibility: OnChangeFn<Record<string, boolean>>
	setColumnPinning: OnChangeFn<SRT_ColumnPinningState>
	setColumnOrder: OnChangeFn<string[]>
	setColumnSizing: OnChangeFn<Record<string, number>>
	restore(): void
	fromConfigs(configs: TableConfigState): void
}
export function useColumnsController(schema?: FieldSchemaModel[]) {
	return useShallow((state: TableConfigState): TableColumnsConfigController => {
		const initialTableState = getInitialTableState(schema)

		// Make sure that the storedTableState is not undefined and has all the required properties
		const storedTableState = {
			...emptyTableState,
			...(state || {}),
		}

		// Reset the table state to the initial state if the state is empty
		// eslint-disable-next-line
		useEffect(() => {
			if (schema?.length && isTableStateEmpty(state)) {
				state.setColumnSizing(() => initialTableState.columnSizing)
				state.setColumnPinning(() => initialTableState.columnPinning)
				state.setColumnVisibility(() => initialTableState.columnVisibility)
			}
		}, [schema])

		// Merge the initialTableState with the storedTableState
		const tableState = {
			...initialTableState,
			...storedTableState,
			// Ensure that columnVisibility is merged correctly
			columnVisibility: {
				...initialTableState.columnVisibility,
				...storedTableState.columnVisibility,
			},
		}

		return {
			columnVisibility: tableState.columnVisibility,
			columnPinning: tableState.columnPinning,
			columnOrder: tableState.columnOrder,
			columnSizing: tableState.columnSizing,
			setColumnVisibility: state.setColumnVisibility as OnChangeFn<
				Record<string, boolean>
			>,
			setColumnPinning:
				state.setColumnPinning as OnChangeFn<SRT_ColumnPinningState>,
			setColumnOrder: state.setColumnOrder as OnChangeFn<string[]>,
			setColumnSizing: state.setColumnSizing as OnChangeFn<
				Record<string, number>
			>,
			restore: () => {
				state.setColumnVisibility(() => initialTableState.columnVisibility)
				state.setColumnPinning(() => initialTableState.columnPinning)
				state.setColumnOrder(initialTableState.columnOrder)
				state.setColumnSizing(() => initialTableState.columnSizing)
			},
			fromConfigs: (configs) => {
				state.setColumnVisibility(() => configs.columnVisibility)
				state.setColumnPinning(() => configs.columnPinning)
				state.setColumnOrder(configs.columnOrder)
				state.setColumnSizing(() => configs.columnSizing)
			},
		}
	})
}
export function getTableStateSnapshot(
	state: Pick<
		TableConfigState,
		'columnVisibility' | 'columnPinning' | 'columnOrder' | 'columnSizing'
	>,
	schema?: FieldSchemaModel[]
) {
	const initialTableState = getInitialTableState(schema)
	const columnVisibility = {
		...initialTableState.columnVisibility,
		...state.columnVisibility,
	}

	return state.columnOrder
		.filter((key) => !systemColumns.includes(key))
		.map((key) => ({
			key,
			enabled: columnVisibility[key],
		}))
}

function isTableStateEmpty(state: TableConfigState) {
	return (
		isEqual(state.columnVisibility, emptyTableState.columnVisibility) &&
		isEqual(state.columnPinning, emptyTableState.columnPinning) &&
		isEqual(state.columnSizing, emptyTableState.columnSizing)
	)
}
/**
 * Migration columns config from version 0 to version 1
 * @param oldState - old state
 * @returns new state
 */
export const migration_run_3_2025 = (oldState: {
	columnsConfig: {
		key: string
		locked: boolean
		enabled: boolean
		width: number
	}[]
}) => {
	try {
		const columnsConfig =
			oldState.columnsConfig.filter(({ key }) => key !== 'control') || []

		return {
			columnOrder: uniq([
				'srt-row-select',
				'srt-row-expand',
				...columnsConfig.map(({ key }) => key),
				'srt-row-spacer',
			]),
			columnPinning: {
				left: uniq([
					'srt-row-select',
					'srt-row-expand',
					...columnsConfig.filter(({ locked }) => locked).map(({ key }) => key),
				]),
				right: [],
			},
			columnVisibility: columnsConfig.reduce(
				(acc, { key, enabled }) => {
					if (!enabled) {
						acc[key] = enabled
					}

					return acc
				},
				{} as Record<string, boolean>
			),
			columnSizing: columnsConfig.reduce(
				(acc, { key, width }) => {
					acc[key] = width

					return acc
				},
				{} as Record<string, number>
			),
		}
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				oldState,
			},
		})

		return undefined
	}
}
